// ============================================================================
// Instruction panel + ESIGN/UETA consent gate.
//
// This is the fix for three of the four failures Ben hit on 2026-07-28: the
// signer now lands on plain-language instructions and a single obvious CTA,
// above the fold, before the document. Nothing below this is reachable until
// consent is given, so there is no way to scroll past the thing you were
// supposed to do.
// ============================================================================

import { useState } from 'react';
import { ChevronDown, FileSignature, Loader2, ShieldCheck } from 'lucide-react';
import {
  CONSENT_CHECKBOX_LABEL, CONSENT_SUMMARY, DISCLOSURE_SECTIONS,
} from './disclosure';

export interface ConsentGateProps {
  documentTitle: string;
  senderName: string;
  noteToSigner: string | null;
  /** Count of required fields, used to set expectations before we start. */
  requiredCount: number;
  isApprover: boolean;
  busy: boolean;
  error: string | null;
  onStart: () => void;
}

export function ConsentGate({
  documentTitle, senderName, noteToSigner, requiredCount, isApprover,
  busy, error, onStart,
}: ConsentGateProps) {
  const [agreed, setAgreed] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const action = isApprover ? 'approve' : 'sign';

  return (
    <main className="mx-auto max-w-2xl p-4">
      <section
        aria-labelledby="signer-instructions-heading"
        className="rounded-lg border border-tcs-border bg-tcs-raised p-5"
      >
        <div className="flex items-start gap-3">
          <FileSignature
            className="mt-0.5 h-6 w-6 shrink-0 text-tcs-accent-ink"
            aria-hidden
          />
          <div className="min-w-0">
            <h1
              id="signer-instructions-heading"
              className="text-lg font-semibold leading-tight text-tcs-text"
            >
              You&rsquo;ve been asked to {action} {documentTitle}
            </h1>
            <p className="mt-1 text-sm text-tcs-text-muted">
              Sent by {senderName}, Terminal Cleaning Services
            </p>
          </div>
        </div>

        <ol className="mt-4 space-y-2 text-sm text-tcs-text">
          <li className="flex gap-2">
            <span aria-hidden className="font-semibold text-tcs-accent-ink">1.</span>
            <span>
              Agree to sign electronically, then choose <strong>Start</strong>.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="font-semibold text-tcs-accent-ink">2.</span>
            <span>
              {isApprover
                ? 'Read the document, then approve it.'
                : <>Tap each highlighted field. Fields marked with a red asterisk
                    (<span className="font-semibold text-st-danger-fg">*</span>) are required.</>}
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="font-semibold text-tcs-accent-ink">3.</span>
            <span>
              {isApprover
                ? 'We email you a copy once everyone has finished.'
                : 'Choose Finish. We email you a signed copy right away.'}
            </span>
          </li>
        </ol>

        {!isApprover && requiredCount > 0 && (
          <p className="mt-3 rounded-md bg-tcs-bg px-3 py-2 text-sm text-tcs-text">
            This document has{' '}
            <strong>
              {requiredCount} required field{requiredCount === 1 ? '' : 's'}
            </strong>
            . It usually takes about a minute.
          </p>
        )}

        {noteToSigner && (
          <div className="mt-4 rounded-md border-l-4 border-tcs-accent-ink bg-[#F4F7FB] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Note from {senderName}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">
              {noteToSigner}
            </p>
          </div>
        )}
      </section>

      {/* ── ESIGN/UETA consent gate ─────────────────────────────────────── */}
      <section
        aria-labelledby="consent-heading"
        className="mt-4 rounded-lg border border-tcs-border bg-tcs-raised p-5"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-tcs-accent-ink" aria-hidden />
          <h2 id="consent-heading" className="text-base font-semibold text-tcs-text">
            Before you begin
          </h2>
        </div>

        <p className="mt-2 text-sm text-tcs-text">{CONSENT_SUMMARY}</p>

        <button
          type="button"
          onClick={() => setShowDisclosure((v) => !v)}
          aria-expanded={showDisclosure}
          aria-controls="esign-disclosure"
          className="mt-2 inline-flex items-center gap-1 rounded text-sm font-medium text-tcs-accent-ink underline underline-offset-2"
        >
          Read the Electronic Record and Signature Disclosure
          <ChevronDown
            className={`h-4 w-4 transition-transform ${showDisclosure ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>

        <div
          id="esign-disclosure"
          hidden={!showDisclosure}
          className="mt-3 max-h-64 overflow-y-auto rounded-md border border-tcs-border bg-tcs-bg p-3"
        >
          {DISCLOSURE_SECTIONS.map((s) => (
            <div key={s.heading} className="mb-3 last:mb-0">
              <h3 className="text-sm font-semibold text-tcs-text">{s.heading}</h3>
              <p className="mt-1 text-sm leading-relaxed text-tcs-text-muted">
                {s.body}
              </p>
            </div>
          ))}
        </div>

        <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer"
          />
          <span className="text-sm text-tcs-text">{CONSENT_CHECKBOX_LABEL}</span>
        </label>

        <button
          type="button"
          onClick={onStart}
          disabled={!agreed || busy}
          className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-md bg-tcs-accent-ink px-4 text-base font-semibold text-tcs-accent-on disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
          {busy ? 'Opening document…' : 'Start'}
        </button>

        {!agreed && (
          <p className="mt-2 text-center text-xs text-tcs-text-muted">
            Tick the box above to continue.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-st-danger-fg">{error}</p>
        )}
      </section>
    </main>
  );
}
